import { Inject, Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, first, mergeMap } from 'rxjs/operators';
import { HttpErrorHandler, HandleError } from './http-error-handler.service';
import { Store } from '@ngrx/store';
import { AppState } from '../states/app.state';
import { SignalrMessageModel } from '../models/SignalrMessageModel';
import { ENV_CONFIG } from '../injection.token';
import { NotificationService } from './notification.service';
import { TIMEOUT_EVENT_TYPE, VALIDATION_EVENT } from 'src/app/extensions/constants/commonConstants';
import { scrollTop, uriNormalize } from 'src/app/extensions/utilities';
import { TranslateService } from '@ngx-translate/core';
import { AddMessage, RemoveMessage } from '../actions/message.actions';

type QueryParams = app.models.QueryParameters;
type Response<T> = app.models.Response<T>;
type SyncResponse = app.models.ServerResponseModel;
type Event = app.models.ServerResponseItemModel;

@Injectable()
export class ApiService {
  public constructor(
    @Inject(ENV_CONFIG) private _envConfig: env.EnvData,
    private http: HttpClient,
    private _httpErrorHandler: HttpErrorHandler,
    private _store: Store<AppState>,
    private _notificationService: NotificationService,
    private readonly _injector: Injector
  ) {
    this.handleError = _httpErrorHandler.createHandleError('ApiService');
  }

  private handleError: HandleError;

  public get<TResult>(url: string, queryParams: QueryParams = undefined): Observable<TResult> {
    if (queryParams != undefined) {
      url = this.mapQueryParams(url, queryParams);
    }

    return this.http.get<TResult>(url).pipe(catchError(this.handleError<TResult>('get')));
  }

  public postGet<T>(url: string, data?: any, customHeaders: any = {}): Observable<T> {
    return this.http.post<T>(url, data, { headers: customHeaders }).pipe(catchError(this.handleError<T>('get')));
  }

  public getRaw(url: string, queryParams: QueryParams = undefined): Observable<string> {
    if (queryParams != undefined) {
      url = this.mapQueryParams(url, queryParams);
    }

    return this.http.get(url, { responseType: 'text' }).pipe(catchError(this.handleError<string>('get')));
  }

  /**  @deprecated Use postAsync instead. */
  public post<TResult>(url: string, data?: any): Observable<TResult> {
    return this.http.post<TResult>(url, data).pipe(catchError(this.handleError('post', data)));
  }

  public postAsync<TResult>(
    url: string,
    data: any,
    syncEvents: string[],
    asyncSuccessEvents?: string[],
    asyncFailEvents?: string[]
  ): Observable<Response<TResult>> {
    let result: Observable<Response<TResult>>;
    const request = this.http.post<TResult>(url, data).pipe(catchError(this.handleError('post', data)));

    if (!asyncSuccessEvents && !asyncFailEvents) {
      result = request.pipe(mergeMap(syncResponse => this.handleSync<TResult>(syncResponse, syncEvents)));
    } else {
      result = request
        .pipe(
          mergeMap(syncResponse =>
            this.handleAsync<TResult>(syncResponse, syncEvents, asyncSuccessEvents, asyncFailEvents)
          )
        )
        .pipe(first(x => !!x));
    }

    return result;
  }

  private checkSyncResponse(events: Event[], syncEvents: string[]) {
    let syncResponse = { isSuccess: false, hasValidationErrors: false };
    syncEvents.forEach(x => {
      if (events.some(y => y.eventType == x)) {
        syncResponse.isSuccess = true;
        return;
      }
    });

    if (events == null || events == undefined) {
      return syncResponse;
    }

    const errors = events
      .filter(x => x.eventType == VALIDATION_EVENT)
      .map(x => x.eventBody)
      .map(x => x.errors);

    if (!syncResponse.isSuccess && errors.some(x => x)) {
      syncResponse.hasValidationErrors = true;
      const _translateService = this._injector.get(TranslateService);
      scrollTop();
      const translatedErrors = errors.map(validationError =>
        _translateService.instant(`validationErrors.${validationError}`)
      );
      this._notificationService.showWarning(translatedErrors.join('\n'), _translateService.instant('common.warning'));
    }

    return syncResponse;
  }

  private handleSync<TResult>(
    syncResponse: any,
    syncEvents: string[],
    alreadyChecked?: boolean
  ): Observable<Response<TResult>> {
    return new Observable<Response<TResult>>(observer => {
      let checkSyncResponse = this.checkSyncResponse(syncResponse.events, syncEvents);

      const response = {
        isSuccess: alreadyChecked ? false : checkSyncResponse.isSuccess,
        response: syncResponse as any as TResult,
        hasValidationErrors: checkSyncResponse.hasValidationErrors,
      } as Response<TResult>;

      observer.next(response);
      observer.complete();
    });
  }

  private handleAsync<TResult>(
    syncResponse: SyncResponse,
    syncEvents: string[],
    asyncSuccessEvents: string[],
    asyncFailEvents: string[]
  ): Observable<Response<TResult>> {
    let isSuccess = false;
    if (syncEvents != null) {
      let checkSyncResponse = this.checkSyncResponse(syncResponse.events, syncEvents);
      isSuccess = checkSyncResponse.isSuccess;
    }
    if (!isSuccess) {
      const timeout = setTimeout(() => {
        asyncFailEvents.push(TIMEOUT_EVENT_TYPE);
        let timeoutMessage = {
          eventType: TIMEOUT_EVENT_TYPE,
          correlationId: syncResponse.correlationId,
        } as SignalrMessageModel;
        this._store.dispatch(AddMessage({ payload: timeoutMessage }));
      }, this._envConfig.TIMEOUT_ASYNC_REQUESTS_SECONDS * 1000);

      return this._store.select(state => {
        let signalREvents = state as any;

        if (signalREvents !== undefined && signalREvents.signalRState.userEvents !== undefined) {
          let event = signalREvents.signalRState.userEvents.find(
            x => x && syncResponse.correlationId == x.correlationId && asyncFailEvents.includes(x.eventType)
          );
          let isSuccess = false;

          if (!event) {
            event = signalREvents.signalRState.userEvents.find(
              x => x && syncResponse.correlationId == x.correlationId && asyncSuccessEvents.includes(x.eventType)
            );
            isSuccess = true;
          }

          if (event) {
            clearTimeout(timeout);
            this._store.dispatch(RemoveMessage({ payload: event }));

            return {
              isSuccess: isSuccess,
              response: {
                correlationId: event.correlationId,
                events: [{ eventType: event.eventType, eventBody: event.eventBody } as Event] as Event[],
              } as SyncResponse as any as TResult,
            } as Response<TResult>;
          }
        }
        return null;
      });
    } else {
      return this.handleSync(syncResponse, syncEvents);
    }
  }

  public upload<TResult>(url: string, data?: any): Observable<TResult> {
    return this.http.post<TResult>(url, data).pipe(catchError(this.handleError<TResult>('post', data)));
  }

  public buildUrl(origin: string, controller: string, request: string, queryParams: QueryParams = undefined): string {
    let url: string = `${uriNormalize(origin)}/${controller}/${request}`;

    if (queryParams != undefined) {
      url = this.mapQueryParams(url, queryParams);
    }

    return url;
  }

  private mapQueryParams(url: string, queryParams: QueryParams): string {
    let mappedUrl = url;
    let isFirstParameter = true;

    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] == undefined || queryParams[key] == null) {
        queryParams[key] = '';
      }

      if (isFirstParameter) {
        isFirstParameter = false;
        mappedUrl = `${mappedUrl}?${key}=${encodeURIComponent(queryParams[key])}`;
      } else {
        mappedUrl = `${mappedUrl}&${key}=${encodeURIComponent(queryParams[key])}`;
      }
    });

    return mappedUrl;
  }
}
